import {
    PROMO_ID_BIRTHDAY,
    PROMO_ID_BLACK_WEEKEND,
    PROMO_ID_SUMMER_SALE,
    PROMO_NAME_CITY_BREAK,
    PROMO_NAME_MARCH,
    PROMO_NAME_SV79,
} from 'components/shared/Promotions/constants'
import { PRODUCT_TYPE_SPA } from 'constants/app'
import { ENV_LOCAL, ENV_TEST, TIMEZONE } from 'constants/system'
import { isCurrentDateWithinRange } from 'helpers/dates'
import { catchErrorByStatus } from 'helpers/errors'
import moment from 'moment'
import { useEffect } from 'react'
import { useGetPromotionQuery } from 'store/api-slice/hooks'
import { setIsAppAccessible, setPromotion } from 'store/general-slice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useApp } from 'store/steps'

export const usePromotions = (promotionId?: string) => {
    const dispatch = useAppDispatch()
    const { language, appMode } = useApp()

    const { isLoading, isError, data, error } = useGetPromotionQuery(
        {
            _locale: language,
            promotionId: promotionId || '',
        },
        {
            skip: !promotionId,
        }
    )

    const { promotion } = useAppSelector((state) => state.general)
    const { isActive, productTypes, promotionId: statePromotionId } = promotion

    // <-- static promotions, api independent
    const isWinterBreakPromo = false
    const isMarchPromoActive = isCurrentDateWithinRange(
        [ENV_LOCAL, ENV_TEST].includes(process.env.REACT_APP_ENV || '')
            ? '2025-02-27'
            : '2025-03-01',
        '2025-03-31'
    )
    const isCityBreakPromoActive = isCurrentDateWithinRange(
        [ENV_LOCAL, ENV_TEST].includes(process.env.REACT_APP_ENV || '')
            ? '2024-03-20'
            : '2025-04-01',
        '2025-06-27'
    )
    const isMayPromotionForStudentsActive = isCurrentDateWithinRange(
        '2024-05-06',
        '2024-06-20'
    )
    const isMayPromotionForWeekendsActive = isCurrentDateWithinRange(
        '2024-05-06',
        '2024-05-26'
    )
    const isSv79PromotionActive = isCurrentDateWithinRange(
        [ENV_LOCAL, ENV_TEST].includes(process.env.REACT_APP_ENV || '')
            ? '2024-08-29'
            : '2024-09-02',
        '2024-12-19'
    )
    // -->

    // conditions for black promotion
    const isBlackPromo = statePromotionId === PROMO_ID_BLACK_WEEKEND
    const isBlackPromoActive = isBlackPromo && isActive
    const isBlackPromoForTickets =
        isBlackPromo &&
        isActive &&
        productTypes?.includes('ticket') &&
        appMode === 'creator'
    const isBlackPromoForCabanas =
        isBlackPromo &&
        isActive &&
        productTypes?.includes('cabana') &&
        appMode === 'creator'
    const isBlackPromoForCreator =
        isBlackPromoForTickets || isBlackPromoForCabanas
    const isBlackPromoForCreatorVisibleOutside =
        isBlackPromo &&
        isActive &&
        (productTypes?.includes('ticket') ||
            productTypes?.includes('cabana')) &&
        appMode !== 'selfcare' &&
        appMode !== 'vouchers'
    const isBlackPromoForUnlimited =
        isBlackPromo &&
        isActive &&
        productTypes?.includes('card') &&
        appMode === 'unlimited-card'
    const isBlackPromoForUnlimitedVisibleOutside =
        isBlackPromo && isActive && productTypes?.includes('card')

    // conditions for birthday promotion
    const isBirthdayPromo = statePromotionId === PROMO_ID_BIRTHDAY
    const isBirthdayPromoForTickets =
        isBirthdayPromo &&
        isActive &&
        productTypes?.includes('ticket') &&
        appMode === 'creator'
    const isBirthdayPromoForSpa =
        isBirthdayPromo &&
        isActive &&
        productTypes?.includes(PRODUCT_TYPE_SPA) &&
        appMode === 'creator'
    const isBirthdayPromoForCabanas =
        isBirthdayPromo &&
        isActive &&
        productTypes?.includes('cabana') &&
        appMode === 'creator'
    const isBirthdayPromoForCreator =
        isBirthdayPromoForTickets ||
        isBirthdayPromoForSpa ||
        isBirthdayPromoForCabanas
    const isBirthdayPromoForCreatorVisibleOutside =
        isBirthdayPromo &&
        isActive &&
        (productTypes?.includes('ticket') ||
            productTypes?.includes(PRODUCT_TYPE_SPA) ||
            productTypes?.includes('cabana'))
    const isBirthdayPromoForUnlimited =
        isBirthdayPromo &&
        isActive &&
        productTypes?.includes('card') &&
        appMode === 'unlimited-card'
    const isBirthdayPromoForUnlimitedVisibleOutside =
        isBirthdayPromo && isActive && productTypes?.includes('card')

    // conditions for summer sale
    const isSummerSale = statePromotionId === PROMO_ID_SUMMER_SALE
    const isSummerSaleForTickets =
        isSummerSale &&
        isActive &&
        productTypes?.includes('ticket') &&
        appMode === 'creator'
    const isSummerSaleForCabanas =
        isSummerSale &&
        isActive &&
        productTypes?.includes('cabana') &&
        appMode === 'creator'
    const isSummerSaleForCreator =
        isSummerSaleForTickets || isSummerSaleForCabanas
    const isSummerSaleForCreatorVisibleOutside =
        isSummerSale &&
        isActive &&
        (productTypes?.includes('ticket') || productTypes?.includes('cabana'))

    // conditions for dark theme
    const isDarkThemeForTickets =
        isBlackPromoForTickets ||
        isBirthdayPromoForTickets ||
        isSummerSaleForTickets
    const isDarkThemeForSpa = isBirthdayPromoForSpa
    const isDarkThemeForCabanas =
        isBlackPromoForCabanas ||
        isBirthdayPromoForCabanas ||
        isSummerSaleForCabanas
    const isDarkThemeForCreator =
        isDarkThemeForTickets || isDarkThemeForSpa || isDarkThemeForCabanas
    const isDarkThemeForUnlimited =
        isBlackPromoForUnlimited || isBirthdayPromoForUnlimited

    const shouldPromoDisableTicketsBundles =
        isSummerSaleForCreatorVisibleOutside

    const promotions = [
        {
            name: PROMO_NAME_MARCH,
            isActiveForTickets: isMarchPromoActive,
            isActiveForRegulations: isMarchPromoActive,
        },
        {
            name: PROMO_NAME_CITY_BREAK,
            isActiveForTickets: isCityBreakPromoActive,
            isActiveForRegulations: isCityBreakPromoActive,
        },
        {
            name: 'Student Promotion 2024',
            isActiveForTickets: false,
            isActiveForRegulations: isCurrentDateWithinRange(
                [ENV_LOCAL, ENV_TEST].includes(process.env.REACT_APP_ENV || '')
                    ? '2024-08-30'
                    : '2024-09-02',
                '2024-10-31'
            ),
        },
        {
            name: PROMO_NAME_SV79,
            isActiveForTickets: isSv79PromotionActive,
            isActiveForRegulations: isSv79PromotionActive,
        },
        {
            name: 'August Promotion',
            isActiveForTickets: isCurrentDateWithinRange(
                '2024-08-20',
                '2024-09-01'
            ),
            isActiveForRegulations: false,
        },
        {
            name: 'Summer Sale',
            isActiveForTickets: false,
            isActiveForRegulations: false,
        },
    ] as const

    const getPromotionByName = (
        name: typeof promotions[number]['name'],
        key?: keyof Omit<typeof promotions[number], 'name'>
    ) => {
        const promotion = promotions.find((promo) => promo.name === name)

        if (!promotion) return false

        if (!key) return promotion.isActiveForTickets

        return promotion[key]
    }

    useEffect(() => {
        // ignore errors on local development
        if (process.env.REACT_APP_ENV === ENV_LOCAL) return

        if (isError && error) {
            catchErrorByStatus({ error }) ||
                dispatch(setIsAppAccessible({ isAppAccessible: false }))
            return
        }
    }, [isError, error])

    useEffect(() => {
        const productTypes = ['ticket', PRODUCT_TYPE_SPA, 'card']

        // static data - only for local development
        if (
            promotionId !== undefined &&
            process.env.REACT_APP_ENV === ENV_LOCAL
        ) {
            dispatch(
                setPromotion({
                    promotionName: 'Birthday promo 2025',
                    startsAt: '2025-02-10 00:00:00.000',
                    endsAt: '2025-02-26 23:59:59.000',
                    promotionId,
                    isActive: true,
                    productTypes,
                })
            )
            return
        }

        if (!data) return

        const { startsAt, isActive } = data

        // if promotion has already started, set its data directly
        if (isActive) {
            dispatch(setPromotion({ ...data, promotionId }))
            return
        }

        const currentDateTime = moment()
        const startDateTime = moment(startsAt).utcOffset(TIMEZONE)
        const timeRemainingToStart = startDateTime.diff(currentDateTime)

        if (timeRemainingToStart <= 0) return

        // if promotion still has time to start,
        // schedule setting its data, manually setting state as active
        const timeoutId = setTimeout(() => {
            dispatch(
                setPromotion({
                    ...data,
                    promotionId,
                    isActive: true,
                    productTypes,
                })
            )
        }, timeRemainingToStart)

        return () => clearTimeout(timeoutId)
    }, [data, promotionId])

    return {
        isLoading,
        isError,
        isDarkThemeForTickets,
        isDarkThemeForSpa,
        isDarkThemeForCabanas,
        isDarkThemeForCreator,
        isDarkThemeForUnlimited,
        shouldPromoDisableTicketsBundles,
        isBlackPromoForTickets,
        isBlackPromoForCabanas,
        isBlackPromoForCreator,
        isBlackPromoActive,
        isBlackPromoForCreatorVisibleOutside,
        isBlackPromoForUnlimited,
        isBlackPromoForUnlimitedVisibleOutside,
        isBirthdayPromoForTickets,
        isBirthdayPromoForSpa,
        isBirthdayPromoForCabanas,
        isBirthdayPromoForCreator,
        isBirthdayPromoForCreatorVisibleOutside,
        isBirthdayPromoForUnlimited,
        isBirthdayPromoForUnlimitedVisibleOutside,
        isWinterBreakPromo,
        isCityBreakPromoActive,
        isMayPromotionForStudentsActive,
        isMayPromotionForWeekendsActive,
        isSummerSaleForTickets,
        isSummerSaleForCabanas,
        isSummerSaleForCreator,
        isSummerSaleForCreatorVisibleOutside,
        isMarchPromoActive,
        getPromotionByName,
    }
}
